import { useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import type { ClaimDisposition } from '@deepguide-ai/types'
import {
  Loader2,
  XCircle,
  ExternalLink,
  Shield,
  Zap,
  Eye,
  AlertTriangle,
  Check,
  X,
  Plus,
} from 'lucide-react'
import { trpc } from '@/lib/trpc'
import { Button } from '@deepguide-ai/ui/button'
import { Input } from '@deepguide-ai/ui/input'
import { toast } from 'sonner'
import { Badge } from '@deepguide-ai/ui/badge'
import { ScrollArea } from '@deepguide-ai/ui/scroll-area'
import { LineageDialog } from '@/components/compliance-lineage/LineageDialog'
import type { TriageClaim, CompatibleTest } from './types'
import {
  displayStatusOf,
  getStatusBadgeColor,
  getStatusLabel,
} from '@/lib/compliance-status'
import { statusBadgeClasses } from '@/lib/status-colors'

export function TriageClaimCard({
  claim,
  isSelected,
  onClick,
  workspaceId,
  auditCycleId,
  readonly = false,
  canManageTestLinks = !readonly,
  onResolved,
  quote,
}: {
  claim: TriageClaim
  isSelected?: boolean
  onClick?: () => void
  workspaceId: string
  auditCycleId?: string
  readonly?: boolean
  canManageTestLinks?: boolean
  onResolved: () => void
  /** The sentence as it reads in the policy, quoted under the claim when it
   *  says more than the claim text (an uploaded policy's full sentence). */
  quote?: string | null
}) {
  const [showDismissInput, setShowDismissInput] = useState(false)
  const [dismissReason, setDismissReason] = useState('')
  const [showLinkPicker, setShowLinkPicker] = useState(false)
  const [showLineage, setShowLineage] = useState(false)
  const [showAllTests, setShowAllTests] = useState(false)
  const [searchInput, setSearchInput] = useState('')

  const resolveMutation = useMutation(
    trpc.claimTriage.resolve.mutationOptions()
  )
  const acknowledgeMutation = useMutation(
    trpc.claimTriage.acknowledgeReview.mutationOptions()
  )
  const unlinkMutation = useMutation(
    trpc.claimTriage.unlinkTest.mutationOptions()
  )

  const compatibleTestsQuery = useQuery({
    ...trpc.claimTriage.compatibleTests.queryOptions({ claimId: claim.id }),
    enabled: canManageTestLinks && showLinkPicker,
  })

  const compatibleTests: CompatibleTest[] = (compatibleTestsQuery.data ??
    []) as CompatibleTest[]

  const recommended = compatibleTests.filter((t) => t.matchTier === 'recommended')
  const sameControl = compatibleTests.filter((t) => t.matchTier === 'same-control')
  const allTests = compatibleTests.filter((t) => t.matchTier === 'all')
  const hasHigherTiers = recommended.length > 0 || sameControl.length > 0
  const effectiveShowAll =
    showAllTests ||
    (!compatibleTestsQuery.isLoading && !hasHigherTiers && compatibleTests.length > 0)

  const searchLower = searchInput.toLowerCase().trim()
  const matchesSearch = (t: CompatibleTest) =>
    !searchLower ||
    (t.title?.toLowerCase().includes(searchLower) ?? false) ||
    (t.taskCode?.toLowerCase().includes(searchLower) ?? false)
  const filteredRecommended = recommended.filter(matchesSearch)
  const filteredSameControl = sameControl.filter(matchesSearch)
  const filteredAll = allTests.filter(matchesSearch)
  const visibleCount =
    filteredRecommended.length +
    filteredSameControl.length +
    (effectiveShowAll ? filteredAll.length : 0)

  const claimLineageQuery = useQuery({
    ...trpc.lineage.getClaimChain.queryOptions({ claimId: claim.id }),
    enabled: showLineage,
  })

  const testLinkProps = (testId: string) => auditCycleId
    ? {
        to: '/workspaces/$workspaceId/auditor-center/$auditCycleId/controls' as const,
        params: { workspaceId, auditCycleId },
        search: { test: testId },
      }
    : {
        to: '/workspaces/$workspaceId/tests/$testId' as const,
        params: { workspaceId, testId },
        search: { from: 'screenata-tests' as const },
      }

  const linkedTests = claim.claimTestLinks ?? []
  const hasPersistedLinkedTest = linkedTests.length > 0
  const hasCreatedFallbackTest = !hasPersistedLinkedTest && !!claim.createdTestId
  const isResolved =
    claim.isResolved && (hasPersistedLinkedTest || claim.disposition === 'DISMISS')
  const linkedTestsCount = claimLineageQuery.data?.tests.length ?? 0

  const handleDispose = (
    disposition: ClaimDisposition,
    cesId?: string,
    reason?: string
  ) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(resolveMutation.mutate as any)(
      {
        claimId: claim.id,
        disposition,
        dismissReason: reason,
        controlEvidenceStatusId: cesId,
        ...(disposition === 'LINK_EXISTING' ? { acknowledgeReview: false } : {}),
      },
      {
        onSuccess: () => {
          setShowDismissInput(false)
          setDismissReason('')
          setShowLinkPicker(false)
          setShowAllTests(false)
          setSearchInput('')
          void compatibleTestsQuery.refetch()
          onResolved()
        },
        onError: (error: Error) => toast.error(error.message),
      }
    )
  }

  return (
    <div
      data-claim-id={claim.id}
      onClick={onClick}
      className={`cursor-pointer space-y-2 rounded-sm border bg-card p-3 text-xs shadow-sm transition-all ${
        isSelected
          ? 'border-blue-400 ring-2 ring-blue-300 dark:border-cobalt-border dark:ring-1 dark:ring-cobalt-border/40'
          : 'hover:border-blue-200 dark:hover:border-cobalt-border'
      } ${isResolved ? 'opacity-75' : ''}`}
    >
      {/* Top row: claim ID + verification */}
      <div className='flex items-start justify-between gap-2'>
        <div className='flex min-w-0 items-center gap-1.5'>
          <span className='rounded-sm border border-status-blue/30 bg-status-blue-bg px-1 py-0.5 font-mono text-[10px] text-status-blue-strong'>
            CLM-{claim.id.slice(-4).toUpperCase()}
          </span>
          {claim.claimSource === 'TSC_GAP' && (
            <span className='rounded-sm border border-red-200 dark:border-status-red/30 bg-red-50 dark:bg-status-red-bg px-1 py-0.5 text-[10px] text-status-red-strong'>
              Gap
            </span>
          )}
          <VerificationIcon verification={claim.verification} />
        </div>
        {!claim.hasCreatedTest && !claim.claimTestLinks?.length && (
          <DispositionBadge
            disposition={claim.disposition}
            needsReview={claim.needsReview}
            hasCreatedTest={claim.hasCreatedTest}
          />
        )}
      </div>

      {/* Claim text */}
      <p className='font-heading line-clamp-3 leading-relaxed text-text-secondary'>
        {claim.needsReview && claim.quoteText && claim.quoteText !== claim.claimText
          ? claim.quoteText
          : claim.claimText}
      </p>
      {claim.needsReview && claim.quoteText && claim.quoteText !== claim.claimText && (
        <p className='line-clamp-2 text-[11px] leading-snug text-text-placeholder line-through'>
          {claim.claimText}
        </p>
      )}
      {!claim.needsReview && quote && quote.trim() !== claim.claimText.trim() && (
        <blockquote className='text-muted-foreground line-clamp-4 border-l-2 pl-2 text-xs leading-relaxed'>
          “{quote.trim()}”
        </blockquote>
      )}

      {/* Linked or created tests */}
      {(hasPersistedLinkedTest || hasCreatedFallbackTest) && (
        <div className='space-y-1 border-t pt-1.5'>
          <div className='flex flex-wrap items-center gap-1.5 text-[9px] uppercase tracking-[0.08em] text-slate-500'>
            <span
              className={`rounded-full px-1.5 py-0.5 font-medium ${
                hasPersistedLinkedTest
                  ? 'bg-muted text-muted-foreground'
                  : 'bg-amber-50 text-amber-700'
              }`}
            >
              {hasPersistedLinkedTest ? `Linked test${linkedTests.length > 1 ? 's' : ''}` : 'Created test'}
            </span>
            {hasCreatedFallbackTest ? (
              <span className='text-status-orange'>Not yet part of lineage</span>
            ) : null}
          </div>
          {linkedTests.map((lt) => {
            const testId = lt.controlEvidenceStatusId
            const ces = lt.controlEvidenceStatus
            const taskCode = ces?.taskCode
            const testTitle = ces?.title || ces?.template?.title
            const displayStatus = ces ? displayStatusOf(ces) : undefined
            const statusColor = getStatusBadgeColor(displayStatus)
            const content = (
              <>
                <span className='bg-muted-foreground inline-flex h-1.5 w-1.5 shrink-0 rounded-full' />
                {taskCode && (
                  <span className='shrink-0 whitespace-nowrap font-mono text-text-placeholder'>
                    {taskCode}
                  </span>
                )}
                <span className='min-w-0 truncate'>
                  {testTitle || 'View test'}
                </span>
              </>
            )
            return (
              <div
                key={testId}
                className='hover:bg-muted/50 flex items-center justify-between gap-1.5 rounded-sm px-1 py-0.5 text-[10px]'
                onClick={(e) => e.stopPropagation()}
              >
                <Link
                  {...testLinkProps(testId)}
                  onClick={(e) => e.stopPropagation()}
                  className='flex min-w-0 flex-1 items-center gap-1.5 text-cobalt-strong hover:underline'
                >
                  {content}
                </Link>
                <Badge status={statusColor} className='h-5 px-1.5 py-0 text-[10px]'>
                  {getStatusLabel(displayStatus)}
                </Badge>
                {canManageTestLinks && <Button
                  type='button'
                  variant='ghost'
                  size='xxsIcon'
                  onClick={(e) => {
                    e.stopPropagation()
                    unlinkMutation.mutate(
                      { claimId: claim.id, controlEvidenceStatusId: testId },
                      { onSuccess: () => { void compatibleTestsQuery.refetch(); onResolved() }, onError: (error) => toast.error(error.message) }
                    )
                  }}
                  disabled={unlinkMutation.isPending}
                  title='Unlink this test'
                  aria-label={`Remove ${testTitle || 'test'} from this claim`}
                  className='shrink-0 text-muted-foreground hover:text-destructive'
                >
                  <X className='size-3' />
                </Button>}
              </div>
            )
          })}
          {hasCreatedFallbackTest && (
            <Link
              {...testLinkProps(claim.createdTestId!)}
              onClick={(e) => e.stopPropagation()}
              className='flex items-center gap-1.5 text-[10px] text-cobalt-strong hover:underline'
            >
              <span className='inline-flex h-1.5 w-1.5 shrink-0 rounded-full bg-status-orange' />
              {claim.createdTestTaskCode && (
                <span className='shrink-0 whitespace-nowrap font-mono text-text-placeholder'>
                  {claim.createdTestTaskCode}
                </span>
              )}
              <span className='line-clamp-1'>
                {claim.createdTestTitle || 'View test'}
              </span>
            </Link>
          )}
        </div>
      )}

      {(hasPersistedLinkedTest || hasCreatedFallbackTest) && (
        <div
          className='flex flex-wrap items-center justify-between gap-2 border-t pt-2'
          onClick={(e) => e.stopPropagation()}
        >
          <div className='flex flex-wrap items-center gap-2'>
            <Button
              type='button'
              variant='ghost'
              size='xxs'
              onClick={() => setShowLineage(true)}
              className='text-[10px] text-cobalt-strong'
            >
              <Eye className='size-3' />
              View evidence chain
            </Button>
            {canManageTestLinks && (
              <Button
                type='button'
                variant='ghost'
                size='xxs'
                onClick={() => {
                  setShowLinkPicker((v) => !v)
                  setShowDismissInput(false)
                }}
                className='text-[10px] text-cobalt-strong'
              >
                <Plus className='size-3' />
                Add test
              </Button>
            )}
          </div>
          {claimLineageQuery.data && hasCreatedFallbackTest && linkedTestsCount === 0 ? (
            <Badge
              variant='outline'
              className='h-5 rounded-full border-status-orange/30 bg-status-orange-bg px-1.5 text-[10px] text-status-orange-strong'
            >
              Created test only
            </Badge>
          ) : null}
        </div>
      )}

      {/* Review action */}
      {!readonly && claim.needsReview && (
        <div
          className='space-y-1.5 rounded-sm border border-amber-200 bg-amber-50 p-2'
          onClick={(e) => e.stopPropagation()}
        >
          <p className='text-[10px] leading-snug text-status-orange-strong'>
            {claim.quoteText && claim.quoteText !== claim.claimText
              ? 'The policy text for this claim was edited.'
              : 'This claim was re-rendered since you last reviewed it. Confirm or dismiss.'}
          </p>
          <div className='flex gap-1.5'>
            <button
              type='button'
              onClick={() =>
                (acknowledgeMutation.mutate as any)(
                  { claimId: claim.id },
                  { onSuccess: () => onResolved() }
                )
              }
              disabled={acknowledgeMutation.isPending}
              className='inline-flex items-center gap-1 rounded-sm bg-amber-600 px-2 py-1 text-[10px] font-medium text-white transition-colors hover:bg-amber-700 disabled:opacity-50'
            >
              {acknowledgeMutation.isPending ? (
                <Loader2 className='h-2.5 w-2.5 animate-spin' />
              ) : (
                <Check className='h-2.5 w-2.5' />
              )}
              {claim.quoteText && claim.quoteText !== claim.claimText
                ? 'Accept Change'
                : 'Confirm'}
            </button>
            <button
              type='button'
              onClick={() => {
                setShowDismissInput(true)
                setShowLinkPicker(false)
              }}
              className='inline-flex items-center gap-1 rounded-sm bg-card px-2 py-1 text-[10px] font-medium text-amber-700 transition-colors hover:bg-amber-100'
            >
              <XCircle className='h-2.5 w-2.5' />
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Action buttons (only for unresolved) */}
      {!hasPersistedLinkedTest && !hasCreatedFallbackTest && (canManageTestLinks || (!readonly && !isResolved && !claim.needsReview)) && (
        <div
          className='flex flex-wrap gap-1.5 border-t pt-2'
          onClick={(e) => e.stopPropagation()}
        >
          {canManageTestLinks && <Button
            variant='outline'
            size='xxs'
            className='text-[10px]'
            onClick={() => {
              setShowLinkPicker((v) => !v)
              setShowDismissInput(false)
            }}
          >
            <Plus className='size-3' />
            Add test
          </Button>}



          {!readonly && !isResolved && !claim.needsReview && claim.dismissable && (
            <button
              onClick={() => {
                setShowDismissInput((v) => !v)
                setShowLinkPicker(false)
              }}
              className='inline-flex items-center gap-1 rounded-sm bg-gray-100 px-2 py-1 text-[10px] font-medium text-gray-600 transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
            >
              <XCircle className='h-2.5 w-2.5' />
              Dismiss
            </button>
          )}
        </div>
      )}

      {/* Dismiss textarea */}
      {!readonly && showDismissInput && (
        <div className='space-y-1.5' onClick={(e) => e.stopPropagation()}>
          <textarea
            value={dismissReason}
            onChange={(e) => setDismissReason(e.target.value)}
            placeholder='Reason for dismissing...'
            rows={2}
            className='w-full rounded-sm border px-2 py-1 text-xs focus:ring-1 focus:ring-gray-400 focus:outline-none'
          />
          <div className='flex gap-1.5'>
            <button
              onClick={() => handleDispose('DISMISS', undefined, dismissReason)}
              disabled={!dismissReason.trim() || resolveMutation.isPending}
              className='inline-flex items-center gap-1 rounded-sm bg-gray-700 px-2 py-1 text-[10px] font-medium text-white disabled:opacity-50'
            >
              {resolveMutation.isPending ? (
                <Loader2 className='h-2.5 w-2.5 animate-spin' />
              ) : null}
              Dismiss
            </button>
            <button
              onClick={() => setShowDismissInput(false)}
              className='rounded-sm px-2 py-1 text-[10px] text-gray-500 hover:bg-gray-100'
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Link existing picker */}
      {canManageTestLinks && showLinkPicker && (
        <div className='space-y-1.5' onClick={(e) => e.stopPropagation()}>
          <p className='text-muted-foreground text-xs'>Choose a test that verifies this practice. Linking does not approve the claim or its evidence.</p>
          {compatibleTestsQuery.isError ? (
            <p role='alert' className='text-destructive text-xs'>{compatibleTestsQuery.error.message}</p>
          ) : compatibleTestsQuery.isLoading ? (
            <div className='flex items-center gap-1.5 text-[10px] text-gray-500'>
              <Loader2 className='h-3 w-3 animate-spin' />
              Loading tests...
            </div>
          ) : (
            <>
              {effectiveShowAll && (
                <Input
                  type='text'
                  placeholder='Search by title or task code...'
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className='text-xs'
                />
              )}
              {visibleCount > 0 ? (
                <ScrollArea
                  className='rounded-sm border bg-card'
                  viewClassName='[&>div]:!block [&>div]:space-y-0.5 max-h-48 p-1'
                >
                  {filteredRecommended.length > 0 && (
                    <>
                      <div className='px-2 pt-1 pb-0.5 text-[9px] font-medium uppercase tracking-wider text-gray-400'>
                        Recommended
                      </div>
                      {filteredRecommended.map((test) => (
                        <TestPickerRow
                          key={test.id}
                          test={test}
                          onSelect={() => handleDispose('LINK_EXISTING', test.id)}
                          disabled={resolveMutation.isPending}
                        />
                      ))}
                    </>
                  )}
                  {filteredSameControl.length > 0 && (
                    <>
                      <div className='px-2 pt-1.5 pb-0.5 text-[9px] font-medium uppercase tracking-wider text-gray-400'>
                        Same control
                      </div>
                      {filteredSameControl.map((test) => (
                        <TestPickerRow
                          key={test.id}
                          test={test}
                          onSelect={() => handleDispose('LINK_EXISTING', test.id)}
                          disabled={resolveMutation.isPending}
                        />
                      ))}
                    </>
                  )}
                  {effectiveShowAll && filteredAll.length > 0 && (
                    <>
                      <div className='px-2 pt-1.5 pb-0.5 text-[9px] font-medium uppercase tracking-wider text-gray-400'>
                        All tests
                      </div>
                      {filteredAll.map((test) => (
                        <TestPickerRow
                          key={test.id}
                          test={test}
                          onSelect={() => handleDispose('LINK_EXISTING', test.id)}
                          disabled={resolveMutation.isPending}
                        />
                      ))}
                    </>
                  )}
                </ScrollArea>
              ) : (
                <p className='text-[10px] text-gray-500'>
                  {searchInput
                    ? 'No tests match your search.'
                    : 'No tests in this workspace.'}
                </p>
              )}
              {!effectiveShowAll && hasHigherTiers && (
                <button
                  type='button'
                  onClick={() => setShowAllTests(true)}
                  className='text-[10px] text-cobalt-strong hover:underline'
                >
                  Browse all tests
                </button>
              )}
            </>
          )}
          <button
            type='button'
            onClick={() => {
              setShowLinkPicker(false)
              setShowAllTests(false)
              setSearchInput('')
            }}
            className='text-[10px] text-gray-500 hover:text-gray-700'
          >
            Cancel
          </button>
        </div>
      )}

      <LineageDialog
        open={showLineage}
        onOpenChange={setShowLineage}
        title='Claim lineage'
        description={claim.claimText}
        workspaceId={workspaceId}
        chain={claimLineageQuery.data?.graph}
        isLoading={claimLineageQuery.isLoading}
        error={claimLineageQuery.error?.message}
        readOnly={readonly}
        forceEmptyState={!!claimLineageQuery.data && linkedTestsCount === 0}
        emptyStateTitle={
          hasCreatedFallbackTest
            ? 'Created test is not linked yet'
            : 'No linked tests yet'
        }
        emptyStateDescription={
          hasCreatedFallbackTest
            ? 'This claim card can point to a test that was created from matching metadata, but there is no persisted claim-to-test lineage yet.'
            : 'This claim has not been linked to a test yet, so there is no downstream evidence chain to display.'
        }
        summary={
          claimLineageQuery.data ? (
            <div className='flex flex-wrap items-center gap-2'>
              <Badge variant='outline' className='rounded-full'>
                {linkedTestsCount} linked test
                {linkedTestsCount === 1 ? '' : 's'}
              </Badge>
              {hasCreatedFallbackTest && linkedTestsCount === 0 ? (
                <Badge
                  variant='outline'
                  className='border-status-orange/30 bg-status-orange-bg text-status-orange-strong'
                >
                  Created test only
                </Badge>
              ) : null}
            </div>
          ) : null
        }
      />
    </div>
  )
}

// ── Small helpers ──

function TestPickerRow({
  test,
  onSelect,
  disabled,
}: {
  test: CompatibleTest
  onSelect: () => void
  disabled?: boolean
}) {
  return (
    <Button variant='ghost' size='sm'
      onClick={onSelect}
      disabled={disabled}
      className='w-full justify-between text-left text-xs'
    >
      <span className='flex min-w-0 items-center gap-1.5'>
        {test.taskCode && (
          <span className='shrink-0 font-mono text-[9px] text-text-placeholder'>
            {test.taskCode}
          </span>
        )}
        <span className='truncate font-medium text-foreground'>
          {test.title || 'Untitled test'}
        </span>
      </span>
      <Badge status={getStatusBadgeColor(test.status)}>{getStatusLabel(test.status)}</Badge>
    </Button>
  )
}

function DispositionBadge({
  disposition,
  needsReview,
  hasCreatedTest,
}: {
  disposition: ClaimDisposition | null
  needsReview: boolean
  hasCreatedTest: boolean
}) {
  if (needsReview) {
    return (
      <span className='inline-flex items-center gap-1 rounded-sm bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700'>
        <AlertTriangle className='h-2.5 w-2.5' />
        Review
      </span>
    )
  }
  if (!disposition && hasCreatedTest) {
    return (
      <span className='inline-flex items-center gap-1 rounded-sm bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700'>
        <Check className='h-2.5 w-2.5' />
        Linked
      </span>
    )
  }
  if (!disposition) {
    return (
      <span className={`inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-[10px] font-medium ${statusBadgeClasses.red}`}>
        <AlertTriangle className='h-2.5 w-2.5' />
        No Test
      </span>
    )
  }
  if (disposition === 'LINK_EXISTING') {
    return (
      <span className='inline-flex items-center gap-1 rounded-sm bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium text-blue-700'>
        <ExternalLink className='h-2.5 w-2.5' />
        Linked
      </span>
    )
  }
  return (
    <span className='inline-flex items-center gap-1 rounded-sm bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500'>
      <XCircle className='h-2.5 w-2.5' />
      Dismissed
    </span>
  )
}

function VerificationIcon({ verification }: { verification: string | null }) {
  if (!verification) return null
  if (
    verification.toLowerCase().includes('code') ||
    verification.toLowerCase().includes('scan')
  ) {
    return (
      <span title='Code/scan verified'>
        <Zap className='h-3 w-3 text-blue-500' />
      </span>
    )
  }
  if (
    verification.toLowerCase().includes('config') ||
    verification.toLowerCase().includes('log')
  ) {
    return (
      <span title='Config/log verified'>
        <Eye className='h-3 w-3 text-purple-500' />
      </span>
    )
  }
  return (
    <span title={verification}>
      <Shield className='h-3 w-3 text-gray-400' />
    </span>
  )
}
